import { LineChart, Map as MapIcon } from "lucide-react";

import { PointKindIcon } from "@/components/market-intelligence/PointKind";
import { Badge } from "@/components/ui/badge";
import { CommandItem } from "@/components/ui/command";
import type { MarketSearchHit } from "@/pages/market-intelligence/market-search";

const ICON_CLASS = "mt-0.5 shrink-0 text-muted-foreground group-data-[selected=true]:text-primary";

interface MarketSearchRowProps {
  hit: MarketSearchHit;
  /** Unique cmdk item value — cmdk keys its keyboard selection off this. */
  value: string;
  onSelect: () => void;
}

/**
 * One Market Intelligence destination in the top-bar search: the point or
 * region, the view it opens ("Forwards › Curve"), and its context line. Same
 * shape as `SearchResultRow`, so the two groups read as one list.
 */
export function MarketSearchRow({ hit, value, onSelect }: MarketSearchRowProps) {
  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className="group items-start gap-2.5 rounded-none px-3 py-2 data-[selected=true]:bg-primary-dim"
    >
      {hit.icon.type === "point" ? (
        <PointKindIcon kind={hit.icon.kind} size={16} title={false} className={ICON_CLASS} />
      ) : hit.icon.type === "region" ? (
        <MapIcon size={16} className={ICON_CLASS} />
      ) : (
        <LineChart size={16} className={ICON_CLASS} />
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm text-foreground">{hit.label}</span>
          {hit.view && (
            <span className="truncate text-sm text-muted-foreground">· {hit.view}</span>
          )}
        </span>
        <span className="truncate text-xs text-text-dim">{hit.detail}</span>
      </span>
      <Badge variant="default" className="ml-auto shrink-0 self-start">
        Market
      </Badge>
    </CommandItem>
  );
}
